/**
 * Product Roadmap Definitions
 * Each product has a step-by-step roadmap matching its kanban statuses.
 * Steps include descriptions, requirements, and operator tips.
 */

export interface RoadmapStep {
  id: string;
  label: string;
  description: string;
  requirements?: string[];
  estimatedDays?: string;
  operatorTip?: string;
}

export interface ProductRoadmap {
  productCode: string;
  statusField: string;
  steps: RoadmapStep[];
  terminalStatuses: string[]; // statuses that end the process (success/rejected)
}

export const PRODUCT_ROADMAPS: Record<string, ProductRoadmap> = {

  // ═══════════════════════════════════
  // DOTACJE PUP
  // ═══════════════════════════════════
  pup_grant: {
    productCode: 'pup_grant',
    statusField: 'status',
    terminalStatuses: ['sukces', 'odrzućony'],
    steps: [
      {
        id: 'nowe_zgloszenie',
        label: 'Nowe zgłoszenie',
        description: 'Klient złożył zgłoszenie. Operator weryfikuje dane kontaktowe i podstawowe informacje.',
        requirements: ['Dane kontaktowe', 'Telefon / email'],
        estimatedDays: '1 dzień',
        operatorTip: 'Zadzwoń do klienta w ciągu 24h od zgłoszenia.',
      },
      {
        id: 'weryfikacja_ok',
        label: 'Weryfikacja OK',
        description: 'Wstępna weryfikacja pozytywna. Klient kwalifikuje się do dotacji PUP.',
        requirements: ['Zaświadczenie z PUP', 'Dowód osobisty'],
        estimatedDays: '2-3 dni',
        operatorTip: 'Sprawdź status rejestracji w PUP i profil klienta.',
      },
      {
        id: 'ostrzezenie',
        label: 'Ostrzeżenie',
        description: 'Wykryto potencjalne problemy — brakujące dokumenty lub ryzyko odrzućenia.',
        operatorTip: 'Skontaktuj się z klientem i wyjaśnij braki.',
      },
      {
        id: 'gotowy_do_pisania',
        label: 'Gotowy do pisania',
        description: 'Wszystkie dane zebrane. Operator przygotowuje wniosek o dotację.',
        requirements: ['Biznesplan', 'Oferty zakupowe', 'Szkolenia'],
        estimatedDays: '3-7 dni',
        operatorTip: 'Użyj AI do wygenerowania listy zakupów i optymalizacji.',
      },
      {
        id: 'umowa_podpisana',
        label: 'Umowa podpisana',
        description: 'Klient podpisał umowę współpracy z Fundacją.',
        estimatedDays: '1-2 dni',
        operatorTip: 'Wyślij potwierdźenie warunków przez portal klienta.',
      },
      {
        id: 'wniosek_zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek o dotację złożony w PUP. Czekamy na decyzję urzędu.',
        estimatedDays: '14-30 dni',
        operatorTip: 'Monitoruj termin rozpatrzenia. Przygotuj klienta na ewentualne uzupełnienia.',
      },
      {
        id: 'sukces',
        label: 'Sukces',
        description: 'Dotacja przyznana! Klient rozpoczyna działalność gospodarczą.',
        operatorTip: 'Wyślij gratulacje i instrukcje dot. rozliczenia dotacji.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Wniosek odrzucony przez PUP.',
        operatorTip: 'Przeanalizuj powód i zaproponuj ponowne złożenie lub alternatywę.',
      },
    ],
  },

  // ═══════════════════════════════════
  // POŻYCZKI — WSPARCIE NA STARCIE
  // ═══════════════════════════════════
  wsparcie_na_starcie: {
    productCode: 'wsparcie_na_starcie',
    statusField: 'loan_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Klient zainteresowany pożyczką. Wstępna rozmowa o warunkach.',
        estimatedDays: '1 dzień',
        operatorTip: 'Przedstaw warunki pożyczki i wymagane dokumenty.',
      },
      {
        id: 'kompletacja_dok',
        label: 'Kompletacja dokumentów',
        description: 'Klient zbiera wymagane dokumenty do wniosku pożyczkowego.',
        requirements: ['Dowód osobisty', 'Zaświadczenie PUP', 'Biznesplan', 'Oferty zakupowe', 'Poręczenie'],
        estimatedDays: '5-14 dni',
        operatorTip: 'Sprawdź kompletność dokumentów w checkliście.',
      },
      {
        id: 'wniosek_zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek pożyczkowy złożony do operatora finansowego.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'w_ocenie',
        label: 'W ocenie',
        description: 'Wniosek jest oceniany przez komisję pożyczkową.',
        estimatedDays: '7-21 dni',
        operatorTip: 'Monitoruj status. Przygotuj odpowiedzi na ewentualne pytania komisji.',
      },
      {
        id: 'zatwierdzony',
        label: 'Zatwierdźony',
        description: 'Pożyczka zatwierdzona. Przygotowanie umowy pożyczkowej.',
        estimatedDays: '3-5 dni',
      },
      {
        id: 'umowa_podpisana',
        label: 'Umowa podpisana',
        description: 'Umowa pożyczkowa podpisana przez obie strony.',
        estimatedDays: '1-2 dni',
      },
      {
        id: 'srodki_wyplacone',
        label: 'Środki wypłacone',
        description: 'Pieniądze przelane na konto klienta.',
        estimatedDays: '3-7 dni',
        operatorTip: 'Potwierdź przelew i przypomnij o warunkach rozliczenia.',
      },
      {
        id: 'w_splacie',
        label: 'W trakcie spłaty',
        description: 'Klient spłaca pożyczkę wg harmonogramu.',
        operatorTip: 'Monitoruj terminowość spłat. W razie opóźnień — kontakt.',
      },
      {
        id: 'zakonczony',
        label: 'Spłacona / Umorzona',
        description: 'Pożyczka spłacona lub umorzona. Sprawa zamknięta.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Wniosek pożyczkowy odrzucony.',
        operatorTip: 'Przeanalizuj powód i zaproponuj alternatywę (dotacja PUP, inna pożyczka).',
      },
    ],
  },

  // ═══════════════════════════════════
  // KFS — Krajowy Fundusz Szkoleniowy
  // ═══════════════════════════════════
  kfs: {
    productCode: 'kfs',
    statusField: 'status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Firma zgłosiła zapotrzebowanie na szkolenie z KFS.',
        estimatedDays: '1 dzień',
        operatorTip: 'Ustal branżę, liczbę pracowników i potrzeby szkoleniowe.',
      },
      {
        id: 'analiza',
        label: 'Analiza potrzeb',
        description: 'Analiza potrzeb szkoleniowych firmy i dopasowanie programu.',
        requirements: ['CEIDG/KRS', 'Zaświadczenie ZUS', 'Zaświadczenie US', 'De minimis', 'Lista pracowników'],
        estimatedDays: '3-5 dni',
        operatorTip: 'Użyj AI do wygenerowania planu szkolenia.',
      },
      {
        id: 'wniosek_zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek KFS złożony do PUP.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'zatwierdzony',
        label: 'Zatwierdźony',
        description: 'PUP zatwierdziło dofinansowanie szkolenia.',
        estimatedDays: '14-30 dni',
        operatorTip: 'Umów termin szkolenia i potwierdź z firmą.',
      },
      {
        id: 'szkolenie_w_toku',
        label: 'Szkolenie w toku',
        description: 'Szkolenie jest realizowane.',
        operatorTip: 'Monitoruj frekwencję i zbieraj dokumentację.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończone',
        description: 'Szkolenie ukończone. Rozliczenie z PUP.',
        operatorTip: 'Przygotuj dokumenty rozliczeniowe i certyfikaty.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Wniosek KFS odrzućony przez PUP.',
        operatorTip: 'Sprawdź powód. Może można złożyć w następnym naborze.',
      },
    ],
  },

  // ═══════════════════════════════════
  // ZUS — Ulgi i raty
  // ═══════════════════════════════════
  zus: {
    productCode: 'zus',
    statusField: 'status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Klient zgłosił problem z ZUS (zadłużenie, ulgi, raty).',
        estimatedDays: '1 dzień',
        operatorTip: 'Ustal rodzaj zadłużenia i kwotę.',
      },
      {
        id: 'analiza',
        label: 'Analiza sytuacji',
        description: 'Analiza sytuacji finansowej i możliwości ulg ZUS.',
        requirements: ['Decyzja ZUS', 'CEIDG/KRS', 'PIT', 'Oświadczenie'],
        estimatedDays: '3-5 dni',
        operatorTip: 'Użyj AI do analizy zadłużenia i opcji ratalnych.',
      },
      {
        id: 'wniosek_zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek o ulgę/raty złożony do ZUS.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'w_rozpatrywaniu',
        label: 'W rozpatrywaniu',
        description: 'ZUS rozpatruje wniosek.',
        estimatedDays: '14-30 dni',
        operatorTip: 'Monitoruj status. Przygotuj ewentualne uzupełnienia.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończone',
        description: 'Sprawa rozwiązana — ulga przyznana lub układ ratalny zatwierdzony.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucone',
        description: 'Wniosek odrzucony przez ZUS.',
        operatorTip: 'Przygotuj odwołanie od decyzji.',
      },
    ],
  },

  // ═══════════════════════════════════
  // BHP — Szkolenia BHP
  // ═══════════════════════════════════
  bhp: {
    productCode: 'bhp',
    statusField: 'status',
    terminalStatuses: ['zakonczony', 'anulowany'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Firma potrzebuje szkolenia BHP dla pracowników.',
        estimatedDays: '1 dzień',
        operatorTip: 'Ustal liczbę pracowników i rodzaj szkolenia (wstępne/okresowe).',
      },
      {
        id: 'ustalanie_terminu',
        label: 'Ustalanie terminu',
        description: 'Ustalanie terminu i miejsca szkolenia z firmą.',
        estimatedDays: '2-5 dni',
      },
      {
        id: 'zaplanowany',
        label: 'Zaplanowane',
        description: 'Szkolenie zaplanowane — termin i miejsce ustalone.',
        requirements: ['Lista pracowników', 'Karta szkolenia', 'CEIDG/KRS'],
        operatorTip: 'Wyślij potwierdźenie terminu i materiały.',
      },
      {
        id: 'w_toku',
        label: 'Szkolenie w toku',
        description: 'Szkolenie BHP jest realizowane.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończone',
        description: 'Szkolenie ukończone. Certyfikaty wydane.',
        operatorTip: 'Wygeneruj certyfikaty i wyślij do firmy.',
      },
      {
        id: 'anulowany',
        label: 'Anulowane',
        description: 'Szkolenie odwołane.',
      },
    ],
  },

  // ═══════════════════════════════════
  // SZKOLENIA (ogólne)
  // ═══════════════════════════════════
  szkolenia: {
    productCode: 'szkolenia',
    statusField: 'status',
    terminalStatuses: ['zakonczony', 'anulowany'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Zgłoszenie zainteresowania szkoleniem.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'analiza_potrzeb',
        label: 'Analiza potrzeb',
        description: 'Analiza potrzeb szkoleniowych i dobór programu.',
        estimatedDays: '2-3 dni',
        operatorTip: 'Dopasuj kurs z katalogu szkoleń do profilu klienta.',
      },
      {
        id: 'oferta_wyslana',
        label: 'Oferta wysłana',
        description: 'Oferta szkoleniowa wysłana do klienta.',
        estimatedDays: '1-2 dni',
      },
      {
        id: 'zaplanowany',
        label: 'Zaplanowane',
        description: 'Szkolenie zaplanowane — termin potwierdzony.',
        estimatedDays: '3-7 dni',
      },
      {
        id: 'w_toku',
        label: 'Szkolenie w toku',
        description: 'Szkolenie jest realizowane.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończone',
        description: 'Szkolenie ukończone.',
        operatorTip: 'Wyślij certyfikat i ankietę satysfakcji.',
      },
      {
        id: 'anulowany',
        label: 'Anulowane',
        description: 'Szkolenie anulowane.',
      },
    ],
  },

  // ═══════════════════════════════════
  // TURNUSY REHABILITACYJNE
  // ═══════════════════════════════════
  turnusy: {
    productCode: 'turnusy',
    statusField: 'trip_status',
    terminalStatuses: ['zakonczony', 'anulowany'],
    steps: [
      {
        id: 'planowany',
        label: 'Planowany',
        description: 'Turnus w fazie planowania — ustalanie ośrodka i terminu.',
        estimatedDays: '7-14 dni',
      },
      {
        id: 'otwarty',
        label: 'Otwarty nabór',
        description: 'Nabór uczestników otwarty. Trwa rekrutacja.',
        operatorTip: 'Promuj turnus i zbieraj zgłoszenia.',
      },
      {
        id: 'pelny',
        label: 'Komplet',
        description: 'Komplet uczestników zebrany.',
        requirements: ['Orzeczenie o niepełnosprawności', 'Skierowanie', 'Wniosek PFRON'],
        operatorTip: 'Zweryfikuj dokumenty PFRON wszystkich uczestników.',
      },
      {
        id: 'potwierdźony',
        label: 'Potwierdzony',
        description: 'Turnus potwierdzony — wszystkie formalności załatwione.',
        estimatedDays: '3-5 dni',
      },
      {
        id: 'w_trakcie',
        label: 'W trakcie',
        description: 'Turnus trwa. Uczestnicy na miejscu.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończony',
        description: 'Turnus zakończony. Rozliczenie z PFRON.',
        operatorTip: 'Przygotuj dokumenty rozliczeniowe PFRON.',
      },
      {
        id: 'anulowany',
        label: 'Odwołany',
        description: 'Turnus odwołany.',
      },
    ],
  },

  // ═══════════════════════════════════
  // WYCIECZKI (EnhaTrip)
  // ═══════════════════════════════════
  wycieczki: {
    productCode: 'wycieczki',
    statusField: 'trip_status',
    terminalStatuses: ['zakonczony', 'anulowany'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Klient zainteresowany wycieczką.',
        estimatedDays: '1 dzień',
        operatorTip: 'Ustal liczbę osób, preferencje trasy i budżet.',
      },
      {
        id: 'rezerwacja',
        label: 'Rezerwacja',
        description: 'Trasa zarezerwowana. Oczekiwanie na wpłatę.',
        estimatedDays: '3-7 dni',
      },
      {
        id: 'potwierdźony',
        label: 'Potwierdzona',
        description: 'Wycieczka potwierdzona — wpłata odebrana.',
        operatorTip: 'Wyślij szczegóły logistyczne do grupy.',
      },
      {
        id: 'w_trakcie',
        label: 'W trakcie',
        description: 'Wycieczka trwa.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończona',
        description: 'Wycieczka zakończona.',
        operatorTip: 'Wyślij ankietę i propozycję kolejnej wycieczki.',
      },
      {
        id: 'anulowany',
        label: 'Odwołana',
        description: 'Wycieczka odwołana.',
      },
    ],
  },

  // ═══════════════════════════════════
  // POŻYCZKI ROZWOJOWE
  // ═══════════════════════════════════
  pozyczki_rozwojowe: {
    productCode: 'pozyczki_rozwojowe',
    statusField: 'loan_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Firma zainteresowana pożyczką rozwojową.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'kompletacja_dok',
        label: 'Kompletacja dokumentów',
        description: 'Zbieranie dokumentacji firmy.',
        requirements: ['CEIDG/KRS', 'Sprawozdanie finansowe', 'Biznesplan', 'Oferty', 'Poręczenie', 'Zaświadczenie ZUS'],
        estimatedDays: '7-14 dni',
      },
      {
        id: 'wniosek_zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek pożyczkowy złożony.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'w_ocenie',
        label: 'W ocenie',
        description: 'Wniosek oceniany przez komisję.',
        estimatedDays: '14-30 dni',
      },
      {
        id: 'zatwierdzony',
        label: 'Zatwierdźony',
        description: 'Pożyczka zatwierdzona.',
        estimatedDays: '3-5 dni',
      },
      {
        id: 'umowa_podpisana',
        label: 'Umowa podpisana',
        description: 'Umowa pożyczkowa podpisana.',
        estimatedDays: '1-2 dni',
      },
      {
        id: 'realizacja',
        label: 'Realizacja',
        description: 'Środki wypłacone, firma realizuje inwestycję.',
        operatorTip: 'Monitoruj postęp realizacji i terminowość spłat.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończone',
        description: 'Pożyczka spłacona.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucone',
        description: 'Wniosek odrzucony.',
      },
    ],
  },

  // ═══════════════════════════════════
  // DOTACJE NA ROZWÓJ
  // ═══════════════════════════════════
  dotacje_rozwojowe: {
    productCode: 'dotacje_rozwojowe',
    statusField: 'dev_grant_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Firma zainteresowana dotacją rozwojową.',
        estimatedDays: '1 dzień',
        operatorTip: 'Bezpłatna analiza kwalifikowalności — bez zobowiązań.',
      },
      {
        id: 'analiza_kwalifikowalnosci',
        label: 'Analiza kwalifikowalności',
        description: 'Bezpłatna analiza czy firma kwalifikuje się do programu dotacyjnego.',
        estimatedDays: '2-3 dni',
        operatorTip: 'Użyj AI do sprawdźenia kwalifikowalności.',
      },
      {
        id: 'kompletacja_dok',
        label: 'Kompletacja dokumentów',
        description: 'Zbieranie dokumentacji firmy.',
        requirements: ['CEIDG/KRS', 'Sprawozdanie', 'Biznesplan', 'Oferty', 'De minimis', 'Pełnomocnictwo'],
        estimatedDays: '7-14 dni',
      },
      {
        id: 'przygotowanie_wniosku',
        label: 'Przygotowanie wniosku',
        description: 'Operator przygotowuje wniosek dotacyjny.',
        estimatedDays: '5-10 dni',
        operatorTip: 'Użyj AI biznesplanu i generatora uzasadnień.',
      },
      {
        id: 'wniosek_zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek złożony w instytucji.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'w_ocenie',
        label: 'W ocenie',
        description: 'Wniosek w ocenie formalnej i merytorycznej.',
        estimatedDays: '30-90 dni',
      },
      {
        id: 'zatwierdzony',
        label: 'Zatwierdźony',
        description: 'Dotacja przyznana!',
        estimatedDays: '7-14 dni',
      },
      {
        id: 'umowa_dofinansowania',
        label: 'Umowa o dofinansowanie',
        description: 'Podpisanie umowy o dofinansowanie z instytucją.',
        estimatedDays: '7-14 dni',
      },
      {
        id: 'realizacja_projektu',
        label: 'Realizacja projektu',
        description: 'Firma realizuje projekt zgodnie z harmonogramem.',
        operatorTip: 'Monitoruj kamienie milowe i wydatki.',
      },
      {
        id: 'rozliczenie',
        label: 'Rozliczenie',
        description: 'Rozliczenie dotacji — weryfikacja wydatków i celów.',
        estimatedDays: '14-30 dni',
      },
      {
        id: 'zakonczony',
        label: 'Zakończony',
        description: 'Projekt rozliczony i zamknięty.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Wniosek odrzucony.',
        operatorTip: 'Przeanalizuj powód. Rozważ poprawkę na kolejny nabór.',
      },
    ],
  },

  // ═══════════════════════════════════
  // CZYSTE POWIETRZE
  // ═══════════════════════════════════
  czyste_powietrze: {
    productCode: 'czyste_powietrze',
    statusField: 'cp_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Właściciel domu zainteresowany programem Czyste Powietrze.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'audyt_energetyczny',
        label: 'Audyt energetyczny',
        description: 'Audyt energetyczny budynku.',
        requirements: ['Akt własności', 'Audyt energetyczny'],
        estimatedDays: '7-14 dni',
        operatorTip: 'Skieruj do certyfikowanego audytora.',
      },
      {
        id: 'kompletacja_dok',
        label: 'Kompletacja dokumentów',
        description: 'Zbieranie dokumentów do wniosku.',
        requirements: ['Zaświadczenie o dochodach', 'Kosztorys', 'Zgoda współwłaścicieli'],
        estimatedDays: '5-10 dni',
      },
      {
        id: 'wniosek_zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek złożony do WFOŚiGW.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'w_ocenie',
        label: 'W ocenie',
        description: 'Wniosek w ocenie WFOŚiGW.',
        estimatedDays: '30-60 dni',
      },
      {
        id: 'zatwierdzony',
        label: 'Zatwierdźony',
        description: 'Dotacja zatwierdzona.',
      },
      {
        id: 'wybrany_wykonawca',
        label: 'Wybrany wykonawca',
        description: 'Wybranie wykonawcy prac termomodernizacyjnych.',
        estimatedDays: '7-14 dni',
      },
      {
        id: 'prace_w_toku',
        label: 'Prace w toku',
        description: 'Prace termomodernizacyjne trwają.',
        operatorTip: 'Monitoruj postęp prac i zgodność z kosztorysem.',
      },
      {
        id: 'odbior',
        label: 'Odbiór / Inspekcja',
        description: 'Odbiór prac i inspekcja końcowa.',
        estimatedDays: '3-7 dni',
      },
      {
        id: 'zakonczony',
        label: 'Zakończone',
        description: 'Projekt zakończony i rozliczony.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Wniosek odrzucony.',
      },
    ],
  },

  // ═══════════════════════════════════
  // USŁUGI KSIĘGOWE
  // ═══════════════════════════════════
  uslugi_ksiegowe: {
    productCode: 'uslugi_ksiegowe',
    statusField: 'accounting_status',
    terminalStatuses: ['zakonczony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Firma zainteresowana usługami księgowymi.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'analiza_potrzeb',
        label: 'Analiza potrzeb',
        description: 'Analiza potrzeb księgowych firmy.',
        estimatedDays: '2-3 dni',
        operatorTip: 'Dopasuj pakiet (KPiR, ryczałt, pełna księgowość).',
      },
      {
        id: 'oferta_wyslana',
        label: 'Oferta wysłana',
        description: 'Oferta cenowa wysłana do klienta.',
        estimatedDays: '1-2 dni',
      },
      {
        id: 'umowa_podpisana',
        label: 'Umowa podpisana',
        description: 'Umowa na usługi księgowe podpisana.',
        requirements: ['CEIDG/KRS', 'NIP/REGON', 'Umowa z bankiem', 'Ostatni PIT'],
      },
      {
        id: 'aktywna_usluga',
        label: 'Aktywna usługa',
        description: 'Usługa księgowa aktywna — bieżąca obsługa.',
      },
      {
        id: 'odnowienie',
        label: 'Odnowienie',
        description: 'Renegocjacja lub odnowienie umowy.',
        operatorTip: 'Zaproponuj nowe warunki z wyprzedzeniem.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończona',
        description: 'Współpraca zakończona.',
      },
    ],
  },

  // ═══════════════════════════════════
  // MARKETING BEAUTY
  // ═══════════════════════════════════
  marketing_beauty: {
    productCode: 'marketing_beauty',
    statusField: 'mkt_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Salon beauty zainteresowany usługami marketingowymi.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'analiza_potrzeb',
        label: 'Analiza potrzeb',
        description: 'Bezpłatna analiza obecności online i potrzeb marketingowych.',
        estimatedDays: '2-3 dni',
        operatorTip: 'Użyj AI do wygenerowania briefu marketingowego.',
      },
      {
        id: 'propozycja_strategii',
        label: 'Propozycja strategii',
        description: 'Przygotowanie propozycji strategii marketingowej.',
        estimatedDays: '3-5 dni',
      },
      {
        id: 'negocjacja',
        label: 'Negocjacja',
        description: 'Negocjacja warunków współpracy.',
        estimatedDays: '3-7 dni',
      },
      {
        id: 'umowa_podpisana',
        label: 'Umowa podpisana',
        description: 'Umowa na usługi marketingowe podpisana.',
      },
      {
        id: 'realizacja',
        label: 'Realizacja',
        description: 'Kampanie marketingowe w toku.',
        operatorTip: 'Raportuj wyniki co miesiąc.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończony',
        description: 'Współpraca zakończona.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Klient zrezygnował.',
      },
    ],
  },

  // ═══════════════════════════════════
  // GASTRO CONSULTING
  // ═══════════════════════════════════
  gastro_consulting: {
    productCode: 'gastro_consulting',
    statusField: 'gastro_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Lokal gastronomiczny zainteresowany konsultingiem.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'audyt_wstepny',
        label: 'Audyt wstępny',
        description: 'Bezpłatny audyt wstępny lokalu.',
        estimatedDays: '3-5 dni',
        operatorTip: 'Użyj AI do audytu operacyjnego.',
      },
      {
        id: 'rekomendacje',
        label: 'Rekomendacje',
        description: 'Prezentacja rekomendacji i planu działań.',
        estimatedDays: '3-5 dni',
      },
      {
        id: 'wdrozenie',
        label: 'Wdrożenie',
        description: 'Wdrażanie rekomendacji w lokalu.',
        requirements: ['Menu', 'Zdjęcia lokalu', 'CEIDG/KRS', 'Decyzja SANEPID'],
      },
      {
        id: 'monitorowanie',
        label: 'Monitorowanie',
        description: 'Monitorowanie efektów wdrożonych zmian.',
        operatorTip: 'Sprawdźaj KPI co miesiąc.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończony',
        description: 'Konsulting zakończony.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Klient zrezygnował.',
      },
    ],
  },

  // ═══════════════════════════════════
  // HUTNIK BUSINESS CLUB
  // ═══════════════════════════════════
  hutnik_biznes: {
    productCode: 'hutnik_biznes',
    statusField: 'hutnik_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Firma zainteresowana sponsoringiem Hutnika.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'kontakt',
        label: 'Kontakt nawiązany',
        description: 'Pierwszy kontakt z decydentem firmy.',
        estimatedDays: '2-3 dni',
        operatorTip: 'Wyślij propozycję partnerską AI.',
      },
      {
        id: 'spotkanie',
        label: 'Spotkanie',
        description: 'Spotkanie prezentacyjne z firmą.',
        estimatedDays: '3-7 dni',
      },
      {
        id: 'negocjacje',
        label: 'Negocjacje',
        description: 'Negocjacja pakietu sponsorskiego.',
        estimatedDays: '7-14 dni',
      },
      {
        id: 'umowa',
        label: 'Umowa podpisana',
        description: 'Umowa sponsorska podpisana.',
        requirements: ['CEIDG/KRS', 'Logo', 'Oferta'],
      },
      {
        id: 'aktywny_sponsor',
        label: 'Aktywny sponsor',
        description: 'Sponsor aktywny — realizacja świadczeń.',
        operatorTip: 'Raportuj ekspozycję i ROI sponsorowi.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończony',
        description: 'Umowa sponsorska zakończona.',
        operatorTip: 'Zaproponuj odnowienie na kolejny sezon.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Firma zrezygnowała.',
      },
    ],
  },

  // ═══════════════════════════════════
  // GRANTY FPN (wewnętrzne projekty)
  // ═══════════════════════════════════
  granty_fpn: {
    productCode: 'granty_fpn',
    statusField: 'grant_fpn_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'pomysl',
        label: 'Pomysł / Idea',
        description: 'Nowy pomysł na projekt grantowy.',
        operatorTip: 'Użyj AI karty projektu do szkicu.',
      },
      {
        id: 'badanie',
        label: 'Badanie programu',
        description: 'Badanie dostępnych programów grantowych.',
        estimatedDays: '3-7 dni',
        operatorTip: 'Sprawdź nabory w monitorze dotacji.',
      },
      {
        id: 'szkic',
        label: 'Szkic wniosku',
        description: 'Przygotowanie wstępnego szkicu wniosku.',
        estimatedDays: '5-10 dni',
      },
      {
        id: 'kompletacja',
        label: 'Kompletacja dokumentów',
        description: 'Zbieranie wymaganych załączników.',
        requirements: ['Opis projektu', 'Budżet', 'Harmonogram', 'Statut', 'KRS'],
        estimatedDays: '7-14 dni',
      },
      {
        id: 'zlozony',
        label: 'Wniosek złożony',
        description: 'Wniosek złożony w systemie grantodawcy.',
        estimatedDays: '1 dzień',
      },
      {
        id: 'w_ocenie',
        label: 'W ocenie',
        description: 'Wniosek w ocenie komisji.',
        estimatedDays: '30-90 dni',
      },
      {
        id: 'zatwierdzony',
        label: 'Zatwierdźony',
        description: 'Grant przyznany!',
      },
      {
        id: 'realizacja',
        label: 'W realizacji',
        description: 'Projekt grantowy w trakcie realizacji.',
        operatorTip: 'Monitoruj kamienie milowe i budżet.',
      },
      {
        id: 'rozliczenie',
        label: 'Rozliczenie',
        description: 'Rozliczenie projektu z grantodawcą.',
        estimatedDays: '14-30 dni',
      },
      {
        id: 'zakonczony',
        label: 'Zakończony',
        description: 'Projekt rozliczony i zamknięty.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Wniosek odrzucony.',
        operatorTip: 'Popraw i złóż na kolejny nabór.',
      },
    ],
  },

  // ═══════════════════════════════════
  // STEAM ACADEMIA (Edukacja STEAM)
  // ═══════════════════════════════════
  steam_education: {
    productCode: 'steam_education',
    statusField: 'steam_status',
    terminalStatuses: ['zakonczony', 'odrzućony'],
    steps: [
      {
        id: 'zapytanie',
        label: 'Zapytanie',
        description: 'Szkoła zainteresowana programem STEAM Academia.',
        estimatedDays: '1 dzień',
        operatorTip: 'Ustal typ szkoły, liczbę uczniów i zainteresowania.',
      },
      {
        id: 'prezentacja',
        label: 'Prezentacja / Demo',
        description: 'Bezpłatna prezentacja programu STEAM dla dyrekcji szkoły.',
        estimatedDays: '3-7 dni',
        operatorTip: 'Użyj AI do wygenerowania raportu gotowości szkoły.',
      },
      {
        id: 'umowa',
        label: 'Umowa',
        description: 'Podpisanie umowy współpracy ze szkołą.',
        requirements: ['Umowa współpracy', 'Dane szkoły (RSPO/NIP)', 'Zgoda dyrektora'],
        estimatedDays: '3-5 dni',
      },
      {
        id: 'onboarding',
        label: 'Onboarding szkoły',
        description: 'Konfiguracja platformy, szkolenie nauczycieli, przygotowanie programu.',
        requirements: ['Lista nauczycieli', 'Program nauczania'],
        estimatedDays: '7-14 dni',
        operatorTip: 'Stwórz konta nauczycieli na platformie STEAM.',
      },
      {
        id: 'aktywny',
        label: 'Aktywna współpraca',
        description: 'Program STEAM aktywny — lekcje, warsztaty, egzaminy.',
        operatorTip: 'Monitoruj postępy uczniów i certyfikaty.',
      },
      {
        id: 'zakonczony',
        label: 'Zakończony',
        description: 'Program STEAM zakończony dla tej szkoły.',
        operatorTip: 'Zaproponuj kontynuację na kolejny rok szkolny.',
      },
      {
        id: 'odrzućony',
        label: 'Odrzucony',
        description: 'Szkoła zrezygnowała z programu.',
      },
    ],
  },
};

/**
 * Get roadmap for a given product code.
 * Returns undefined if product has no defined roadmap.
 */
export function getRoadmap(productCode: string): ProductRoadmap | undefined {
  return PRODUCT_ROADMAPS[productCode];
}

/**
 * Get current step index in a product's roadmap.
 * Returns -1 if status not found in steps.
 */
export function getCurrentStepIndex(productCode: string, currentStatus: string): number {
  const roadmap = PRODUCT_ROADMAPS[productCode];
  if (!roadmap) return -1;
  return roadmap.steps.findIndex(s => s.id === currentStatus);
}
